import React, { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Download as DownloadIcon } from '@mui/icons-material'
import { Box } from '@mui/material'
import { CatalogsApi } from '../../api/catalogs.api'
import { inventoryChecksService } from '../../store/inventoryChecks'
import InventoryCheckResourceList from '../inventoryCheck/ResourceList.inventoryCheck'
import InventoryCheckWasteList from '../inventoryCheck/WasteList.inventoryCheck'
import ModalFormWaste from '../inventoryCheck/ModalFormWaste.inventoryCheck'
import DisplayModeButton from '../common/button/DisplayMode.button'
import ResponsiveIconButton from '../common/button/ResponsiveIconButton'
import ModalExport from '../material/ModalExport.material'
import useSnackbar from '../../hooks/useSnackbar.hooks'
import { catalogsService } from '../../store/catalogs'
import LoaderOverlay from '../layout/LoaderOverlay.layout'
import { ListRef } from '../common/List.common'
import { Catalog } from '../../models/catalogs.models'
import { MaterialType } from '../../models/materials.models'
import { InventoryCheckWaste } from '../../models/inventoryChecks.model'
import { ManageInventoryCheckWaste } from '../../models/inventoryChecks.model'
import Modal from '../layout/Modal.layout'

interface InventoryCheckPageProps {
  catalog: Catalog
  type: MaterialType
  isInventoryCheck: boolean
}
type DisplayMode = 'resource' | 'waste'

const InventoryCheckPage: React.FC<InventoryCheckPageProps> = ({
  catalog,
  type,
  isInventoryCheck,
}) => {
  const show = useSnackbar()
  const { t } = useTranslation()
  const [exportModal, setExportModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [displayMode, setDisplayMode] = useState<DisplayMode>('resource')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedWaste, setSelectedWaste] = useState<InventoryCheckWaste | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const listRef = useRef<ListRef>(null)

  const handleDisplayChange = (mode: DisplayMode) => {
    setDisplayMode(mode)
  }

  const handleExport = useCallback(
    async (exportType: CatalogsApi.CatalogExportType) => {
      setLoading(true)
      try {
        await catalogsService.downloadDiagnostic(catalog, exportType, {
          selectAll: true,
          ids: [],
        })
        setExportModal(false)
      } catch (err: any) {
        show(err)
      }
      setLoading(false)
    },
    [catalog, show],
  )

  const getWastes = useCallback(
    async (params: any) => {
      return await inventoryChecksService.getListWastes({
        ...params,
        catalog: catalog._id,
      })
    },
    [catalog],
  )

  const getResources = useCallback(
    async (params: any) => {
      return await inventoryChecksService.getListResources({
        ...params,
        catalog: catalog._id,
      })
    },
    [catalog],
  )

  const handleOpenModal = useCallback((waste?: InventoryCheckWaste) => {
    setSelectedWaste(waste || null)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedWaste(null)
    setIsModalOpen(false)
  }, [])

  const handleSubmitWaste = useCallback(
    async (data: ManageInventoryCheckWaste) => {
      const files = data.supportingDocumentsFile || []
      if (selectedWaste?._id) {
        await inventoryChecksService.updateWaste(selectedWaste._id, data, files)
      } else {
        await inventoryChecksService.createWaste({ ...data, catalog: catalog._id }, files!)
      }
      handleCloseModal()
      listRef.current?.updateValues()
    },
    [selectedWaste, catalog, handleCloseModal],
  )

  const handleDeleteWaste = useCallback(
    async (id: string) => {
      await inventoryChecksService.deleteWaste(id)
    },
    [],
  )


  return (
    <Box p={2}>
      {loading && <LoaderOverlay />}
      <Box display="flex" flexDirection="column" mb={3} gap={2}>
        <Box display="flex" justifyContent="flex-end">
          <ResponsiveIconButton startIcon={<DownloadIcon />} onClick={() => setExportModal(true)}>
            {t('global:actions.export')}
          </ResponsiveIconButton>
        </Box>
        <Box display="flex" gap={1}>
          <DisplayModeButton
            selected={displayMode === 'resource'}
            onClick={() => handleDisplayChange('resource')}>
            PEM
          </DisplayModeButton>
          <DisplayModeButton
            selected={displayMode === 'waste'}
            onClick={() => handleDisplayChange('waste')}>
            D
          </DisplayModeButton>
        </Box>
      </Box>
      <Box>
        {displayMode === 'resource' && <InventoryCheckResourceList getValues={getResources} />}
        {displayMode === 'waste' && (
          <InventoryCheckWasteList
            ref={listRef}
            getValues={getWastes}
            onValueClick={handleOpenModal}
            onAddWaste={handleOpenModal}
          />
        )}
      </Box>

      {exportModal && (
        <ModalExport
          showCerfa={true}
          type={type}
          onClose={() => setExportModal(false)}
          onSubmit={handleExport}
          isInventoryCheck={isInventoryCheck}
        />
      )}
      {isModalOpen && (
        <ModalFormWaste
          inventoryCheckWaste={selectedWaste}
          onSubmit={handleSubmitWaste}
          onSuccess={() => {
            show(
              selectedWaste?._id
                ? t('inventoryChecks:waste.snackbar.modifySuccess')
                : t('inventoryChecks:waste.snackbar.addSuccess'),
              'success',
            )
          }}
          onClose={handleCloseModal}
          actions={
            selectedWaste?._id
              ? [
                  {
                    onClick: () => setIsDeleteModalOpen(true),
                    color: 'error',
                    label: t('global:actions.delete'),
                  },
                ]
              : undefined
          }
        />
      )}
      {isDeleteModalOpen && (
        <Modal
          confirmLabel={t('global:actions.delete')}
          title={t('inventoryChecks:waste.action.confirm')}
          onSuccess={() => {
            show(t('inventoryChecks:waste.snackbar.deleteSuccess'), 'success')
            setIsDeleteModalOpen(false)
            handleCloseModal()
            listRef.current?.updateValues()
          }}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => handleDeleteWaste(selectedWaste?._id as string)}
        />
      )}
    </Box>
  )
}

export default InventoryCheckPage
